import React, { useState } from "react";
import { Form, Button, Checkbox } from "antd";
import styled from "styled-components";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import PasswordInputWithValidation from "../common/PasswordInputWithValidation";
import { validatePassword } from "@/shared/utils/passwordValidation";
import TermsOfServiceModal from "@/shared/components/ToS/TermsOfServiceModal";

const PasswordSetupContainer = styled.div`
	text-align: center;
	padding: 2rem 0;
`;

const PasswordForm = styled(Form)`
	text-align: left;

	.ant-form-item {
		margin-bottom: 1.5rem;
	}

	.ant-form-item-label {
		padding-bottom: 0.5rem;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #333;
	}

	.ant-input-affix-wrapper {
		height: 40px;
		border-radius: 20px;
		border: 1px solid #d9d9d9;

		&:hover,
		&:focus {
			border-color: ${CUSTOM_COLORS.PRIMARY};
		}
	}

	.ant-input {
		font-size: 0.9rem;
	}
`;

const StyledPasswordInput = styled(PasswordInputWithValidation)`
	.ant-input-affix-wrapper {
		height: 40px;
		border-radius: 20px;
		border: 1px solid #d9d9d9;

		&:hover,
		&:focus {
			border-color: ${CUSTOM_COLORS.PRIMARY};
		}
	}
`;

const ButtonGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	margin-top: 2rem;
`;

const TermsLink = styled.button`
	display: inline;
	padding: 0;
	border: none;
	background: none;
	font: inherit;
	cursor: pointer;
	color: ${CUSTOM_COLORS.PRIMARY};
	text-decoration: underline;

	&:hover {
		opacity: 0.85;
	}
`;

// Removed unused disabled button

// Primary register button will use AntD Button with primary type

interface PasswordSetupStepProps {
	username: string;
	onPasswordSubmit: (password: string) => void;
	loading: boolean;
	termsAlreadyAccepted?: boolean;
}

const PasswordSetupStep: React.FC<PasswordSetupStepProps> = ({
	// username,
	onPasswordSubmit,
	loading,
	termsAlreadyAccepted = false,
}) => {
	const [form] = Form.useForm();
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isPasswordValid, setIsPasswordValid] = useState(false);
	const [isConfirmValid, setIsConfirmValid] = useState(false);
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [tosModalOpen, setTosModalOpen] = useState(false);
	const canSubmit = isPasswordValid && isConfirmValid && (termsAlreadyAccepted || acceptedTerms);

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setPassword(value);
		const validation = validatePassword(value);
		setIsPasswordValid(validation.isValid);
	};

	const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setConfirmPassword(value);
		setIsConfirmValid(value === password && value.length > 0);
	};

	const handleTermsChange = (checked: boolean) => {
		setAcceptedTerms(checked);
		// Clear any previous validation error as soon as the user accepts
		if (checked) {
			form.setFields([{ name: "terms", errors: [] }]);
		}
	};

	// Triggered after Form validators pass (password, confirm password, terms)
	const handleSubmit = () => {
		if (canSubmit) {
			onPasswordSubmit(password);
		}
	};

	const handleRegisterClick = () => {
		form.submit();
	};

	return (
		<PasswordSetupContainer>
			<PasswordForm form={form} layout="vertical" onFinish={handleSubmit}>
				<Form.Item
					name="password"
					label="Password"
					rules={[
						{
							validator: (_, value) => {
								if (!value) {
									return Promise.reject(new Error("Please enter a password"));
								}
								const validation = validatePassword(value);
								if (!validation.isValid) {
									return Promise.reject(new Error(validation.errors[0]));
								}
								return Promise.resolve();
							},
						},
					]}
				>
					<StyledPasswordInput
						value={password}
						onChange={handlePasswordChange}
						placeholder="Enter Password"
						size="large"
						showValidation={true}
					/>
				</Form.Item>

				<Form.Item
					name="confirmPassword"
					label="Confirm password"
					rules={[
						{
							validator: (_, value) => {
								if (!value) {
									return Promise.reject(new Error("Please confirm your password"));
								}
								if (value !== password) {
									return Promise.reject(new Error("Passwords do not match!"));
								}
								return Promise.resolve();
							},
						},
					]}
				>
					<StyledPasswordInput
						value={confirmPassword}
						onChange={handleConfirmPasswordChange}
						placeholder="Re-enter Password"
						size="large"
						showValidation={false}
					/>
				</Form.Item>

				{!termsAlreadyAccepted && (
					<Form.Item
						name="terms"
						valuePropName="checked"
						style={{ marginBottom: 0 }}
						rules={[
							{
								validator: (_, value) =>
									value
										? Promise.resolve()
										: Promise.reject(
												new Error("You must accept the Terms of Service to continue."),
											),
							},
						]}
					>
						<Checkbox checked={acceptedTerms} onChange={(e) => handleTermsChange(e.target.checked)}>
							I have read and agree to the{" "}
							<TermsLink
								type="button"
								onClick={(e) => {
									// Prevent the click from toggling the checkbox
									e.preventDefault();
									e.stopPropagation();
									setTosModalOpen(true);
								}}
							>
								Terms of Service
							</TermsLink>
						</Checkbox>
					</Form.Item>
				)}
			</PasswordForm>

			<ButtonGroup>
				<Button
					type="primary"
					onClick={handleRegisterClick}
					loading={loading}
					disabled={!canSubmit}
					style={{ height: 40, borderRadius: 20 }}
				>
					Register
				</Button>
			</ButtonGroup>

			{!termsAlreadyAccepted && (
				<TermsOfServiceModal open={tosModalOpen} onClose={() => setTosModalOpen(false)} />
			)}
		</PasswordSetupContainer>
	);
};

export default PasswordSetupStep;
